// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { useTimeout } from '@/hooks/use-timeout';
import { deleteItemField } from '@/items/api/delete-item-field';
import { ItemField } from '@/items/api/types';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  isOpen: boolean;

  /**
   * The field to be deleted.
   */
  field: Pick<ItemField, 'key' | 'label' | 'metadataCount'>;

  /**
   * The key of the item containing the field.
   */
  itemKey: string;

  /**
   * Callback fired when the dialog should close.
   */
  onClose: () => void;
}

/**
 * A confirmation dialog for deleting item fields.
 * Requires explicit confirmation by typing metadata count.
 */
export function DeleteItemFieldDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  // Schema requiring exact match of metadata count for confirmation
  const formSchema = z.object({ count: z.literal(props.field.metadataCount) });

  const { formState, reset, register, handleSubmit } = useForm<
    z.infer<typeof formSchema>
  >({ resolver: zodResolver(formSchema) });

  const mutation = useMutation({
    mutationFn: () => deleteItemField(props.itemKey, props.field.key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['item', props.itemKey],
      });

      close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.standard);

  const isDisabled = mutation.isPending || !formState.isValid;

  const handleValidSubmission = () => {
    mutation.mutate();
  };

  const close = () => {
    props.onClose();
    timeout.start();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <DialogTitle>Delete field</DialogTitle>
      <DialogContent dividers>
        <Typography marginBottom={2}>
          Deleting {props.field.label} will <b>DELETE DATA</b> for any asset
          that has metadata in the field.
        </Typography>
        <Typography marginBottom={2}>
          {user.organization.name} has{' '}
          <b>{pluralize(props.field.metadataCount, { singular: 'asset' })}</b>{' '}
          with values in this field.
        </Typography>
        <TextField
          fullWidth
          size="small"
          error={Boolean(formState.errors.count)}
          helperText="Type the number to confirm deleting metadata"
          InputProps={register('count', { valueAsNumber: true })}
          placeholder={String(props.field.metadataCount)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>No, cancel</Button>
        <Button
          type="submit"
          color="error"
          variant="contained"
          disabled={isDisabled}
        >
          Yes, delete permanently
        </Button>
      </DialogActions>
    </Dialog>
  );
}
